// src/pages/Prompts.js
import React, { useState, useEffect } from "react";
import {
  Search,
  Copy,
  ExternalLink,
  Tag,
  BookOpen,
  PenTool,
  Briefcase,
  Zap,
  Users,
  TrendingUp,
  MessageCircle,
} from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom"; // Assuming you'll use React Router
import { db } from "../firebase/config";
import Header from "../Components/Header";
import toast from "react-hot-toast";

// --- Icons Mapping ---
const categoryIcons = {
  Writing: PenTool,
  CEO: Briefcase,
  Automation: Zap,
  HR: Users,
  Sales: TrendingUp,
  Marketing: MessageCircle,
  All: BookOpen, // Default icon for 'All' category
};

const AIPrompts = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [prompts, setPrompts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    "All",
    "Writing",
    "CEO",
    "Automation",
    "HR",
    "Sales",
    "Marketing",
  ];

  // Fetch prompts from Firestore on component mount
  useEffect(() => {
    const fetchPrompts = async () => {
      setLoading(true);
      setError(null);
      try {
        const querySnapshot = await getDocs(collection(db, "aiprompts"));
        const fetchedPrompts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPrompts(fetchedPrompts);
      } catch (err) {
        console.error("Error fetching prompts: ", err);
        setError("Failed to load prompts. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, []); // Empty dependency array means this runs once on mount

  const filteredPrompts = prompts.filter((prompt) => {
    const matchesSearch =
      prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (prompt.tags &&
        Array.isArray(prompt.tags) &&
        prompt.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ));
    const matchesCategory =
      selectedCategory === "All" || prompt.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Prompt copied to clipboard!");
  };

  // const openInAI = (promptText, platform) => {
  //   const encodedPrompt = encodeURIComponent(promptText);

  //   const urls = {
  //     // ChatGPT - works with the current approach
  //     chatgpt: `https://chat.openai.com/?q=${encodedPrompt}`,

  //     // Claude - opens new conversation, user needs to paste manually
  //     claude: `https://claude.ai/chat`,

  //     // Gemini - opens new conversation, user needs to paste manually
  //     gemini: `https://gemini.google.com/app`,
  //   };

  //   // For ChatGPT, open with pre-filled prompt
  //   if (platform === "chatgpt") {
  //     window.open(urls[platform], "_blank");
  //   } else {
  //     // For Claude and Gemini, copy to clipboard and open the platform
  //     navigator.clipboard.writeText(promptText);
  //     window.open(urls[platform], "_blank");

  //     // Show a toast notification
  //     toast.success(
  //       `Prompt copied to clipboard! Paste it in ${
  //         platform.charAt(0).toUpperCase() + platform.slice(1)
  //       }`
  //     );
  //   }
  // };
const openInAI = (promptText, platform) => {
  const encodedPrompt = encodeURIComponent(promptText);
  
  const urls = {
    // ChatGPT - Using ?q parameter (will auto-send, but most reliable)
    // Alternative: Use ?model=gpt-4&q= for specific model
    chatgpt: `https://chat.openai.com/?q=${encodedPrompt}`,
    
    // Claude - No URL parameter support, copy to clipboard
    claude: `https://claude.ai/chat`,
    
    // Gemini - No URL parameter support, copy to clipboard
    gemini: `https://gemini.google.com/app`
  };
  
  if (platform === 'chatgpt') {
    // For ChatGPT, the prompt will be auto-sent due to ?q parameter
    window.open(urls[platform], "_blank");
    toast.success('ChatGPT opened with your prompt!');
  } else {
    // For Claude and Gemini, copy to clipboard and open
    navigator.clipboard.writeText(promptText);
    window.open(urls[platform], "_blank");
    toast.success(`Prompt copied to clipboard! Paste it in ${platform.charAt(0).toUpperCase() + platform.slice(1)}`);
  }
};
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#0a1344" }}
      >
        <p className="text-white text-lg">Loading prompts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#0a1344" }}
      >
        <p className="text-red-400 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex flex-col">
        <Header
          PageName={"Mes Prompts"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="flex flex-col bg-bgColor rounded-[15px] border border-borderColor p-6">
        {/* Header */}
        <div className="text-center ">
          <h1 className="text-left text-2xl font-bold text-white mb-4">
            Mes Prompts
          </h1>
          <p className="text-left text-sm text-blue-200 mb-6">
            Des invites d'IA prêtes à l'emploi pour tous les besoins
          </p>
        </div>

        {/* Search and Categories */}
        <div className="mb-8">
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Invites de recherche..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category Navigation */}
          <div className="flex flex-wrap text-xs justify-start gap-3">
            {categories.map((category) => {
              const Icon = categoryIcons[category];
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex items-center tex-sm gap-2 px-2 py-1 rounded-xl transition-colors ${
                    selectedCategory === category
                      ? "bg-blue-600 text-white"
                      : "bg-white/10 text-blue-200 hover:bg-white/20"
                  }`}
                >
                  {category !== "All" &&
                    Icon &&
                    React.createElement(Icon, { className: "w-4 h-4" })}
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* Prompts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  {categoryIcons[prompt.category] &&
                    React.createElement(categoryIcons[prompt.category], {
                      className: "w-5 h-5 text-blue-400",
                    })}
                  <span className="px-2 py-1 bg-blue-600/20 text-blue-200 rounded-full text-xs">
                    {prompt.category}
                  </span>
                </div>
              </div>

              <h3 className="text-start text-lg font-semibold text-white mb-2">
                {prompt.title}
              </h3>
              <p className="text-start text-gray-300 text-sm mb-4">
                {prompt.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-4">
                {prompt.tags &&
                  Array.isArray(prompt.tags) &&
                  prompt.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-600/20 text-gray-300 rounded text-xs"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={() => copyToClipboard(prompt.description)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copier l'invite
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => openInAI(prompt.description, "chatgpt")}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
                    title="Opens ChatGPT with prompt pre-filled"
                  >
                    <ExternalLink className="w-3 h-3" />
                    ChatGPT
                  </button>
                  <button
                    onClick={() => openInAI(prompt.description, "claude")}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
                    title="Copies prompt and opens Claude AI"
                  >
                    <Copy className="w-3 h-3" />
                    Claude
                  </button>
                  <button
                    onClick={() => openInAI(prompt.description, "gemini")}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
                    title="Copies prompt and opens Gemini"
                  >
                    <Copy className="w-3 h-3" />
                    Gemini
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPrompts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              Aucune invite trouvée correspondant à vos critères.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIPrompts;
